import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-data-obfuscation',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './data-obfuscation.component.html',
  styleUrl: './data-obfuscation.component.scss'
})
export class DataObfuscationComponent implements OnInit {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const caseStudySchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/data-obfuscation",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/data-obfuscation",
          "name": "Kalpita Data Obfuscation to Improve Data Security – Case Study",
          "description": "Case study on how Kalpita Technologies created a data obfuscation tool that transforms sensitive personally identifiable information (PII) into non-sensitive anonymous form.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-01-28"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/data-obfuscation#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study", "name": "Case Studies" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/data-obfuscation", "name": "Kalpita Data Obfuscation" } }
          ]
        },
        {
          "@type": "Article",
          "headline": "Kalpita Data Obfuscation to Improve Data Security",
          "articleBody": "Kalpita Technologies created a data obfuscation tool designed to enhance data security by transforming sensitive or personally identifiable information (PII) into a non-sensitive and anonymous form...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/obf-casestudy-soln.webp"
        },
        {
          "@type": "HowTo",
          "name": "How to Implement Data Obfuscation for PII Protection",
          "step": [
            { "@type": "HowToStep", "name": "Identify PII", "text": "Assess the organization's data landscape to identify sensitive PII requiring protection." },
            { "@type": "HowToStep", "name": "Define Rules", "text": "Configure keyword management and specify how confidential data is replaced." },
            { "@type": "HowToStep", "name": "Build Functions", "text": "Create mapping tables and develop user-defined functions for custom logic." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/data-obfuscation#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the Kalpita Data Obfuscation tool?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It is a data security solution that transforms sensitive PII into non-sensitive form by masking it with symbols, numbers, or alphabets."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(caseStudySchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
